import type { IAnyModelType } from 'mobx-state-tree';

import type { InteractivityAction } from './InteractivityAction';
import type { InteractivityActionSnapshotMetadata } from './interactivityActionSnapshotMetadata';
import type { InteractivityActionTypeValue } from './InteractivityActionTypeValue';

export enum InteractivityActionConstraint {
  None = 'none',
  Unique = 'unique',
}

export interface InteractivityActionInstanceMetadata {
  modelType: IAnyModelType;

  constraint: InteractivityActionConstraint;

  innerInteractivityActions?: {
    fromInstance(instance: unknown): InteractivityAction[];
  };
}

export interface InteractivityActionMetadata
  extends InteractivityActionInstanceMetadata,
    InteractivityActionSnapshotMetadata {
  innerInteractivityActions: InteractivityActionInstanceMetadata['innerInteractivityActions'] &
    InteractivityActionSnapshotMetadata['innerInteractivityActions'];
}

const modelTypeToMetadataMap = new Map<IAnyModelType, InteractivityActionMetadata>();

const typeIdToMetadataMap = new Map<
  InteractivityActionTypeValue,
  InteractivityActionMetadata
>();

export function setInteractivityActionMetadata(
  instanceMetadata: InteractivityActionInstanceMetadata,
  snapshotMetadata: InteractivityActionSnapshotMetadata,
) {
  const metadata = {
    ...snapshotMetadata,
    ...instanceMetadata,

    innerInteractivityActions: {
      ...snapshotMetadata.innerInteractivityActions,
      ...instanceMetadata.innerInteractivityActions,
    },
  } as InteractivityActionMetadata;

  modelTypeToMetadataMap.set(metadata.modelType, metadata);
  typeIdToMetadataMap.set(metadata.typeId, metadata);
}

export function tryGetInteractivityActionMetadata(
  interactivityActionType: IAnyModelType | InteractivityActionTypeValue,
): InteractivityActionMetadata | undefined {
  const metadata =
    typeof interactivityActionType === 'string'
      ? typeIdToMetadataMap.get(interactivityActionType)
      : modelTypeToMetadataMap.get(interactivityActionType);

  return metadata;
}

export function getInteractivityActionMetadata(
  interactivityActionType: IAnyModelType | InteractivityActionTypeValue,
): InteractivityActionMetadata {
  const metadata = tryGetInteractivityActionMetadata(interactivityActionType);

  if (!metadata) {
    throw new Error(
      `error getting metadata for unknown interactivity action type - ${interactivityActionType}`,
    );
  }

  return metadata;
}
